import { Currency } from '../enums/Currency.enum';

export interface DepositAllowance {
  hasLimit: boolean;
  topePen: number;
  topeUsd: number;
  remainingPen: number;
  remainingUsd: number;
}

export const formatDepositMoney = (amount: number, currency: string): string => {
  if (!Number.isFinite(amount)) {
    return '';
  }

  const symbol = currency === Currency.USD ? 'USD' : 'S/';
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  const formatted = amount
    .toFixed(hasCents ? 2 : 0)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${symbol} ${formatted}`;
};

const CENTS_IN_A_UNIT = 100;

// ⚠ D137 · a tope carrying cents is a cent short of its band edge (4 999.99 for *menos de
// s/ 5 000*), so the sentence quotes the edge and says *menos de*; a whole tope keeps *hasta*.
const topeBound = (tope: number): { limit: string; amount: number } => {
  const cents = Math.round(tope * CENTS_IN_A_UNIT);

  return Number.isFinite(cents) && cents % CENTS_IN_A_UNIT !== 0
    ? { limit: 'menos de', amount: (cents + 1) / CENTS_IN_A_UNIT }
    : { limit: 'hasta', amount: tope };
};

export const depositLimitSentence = (
  allowance: DepositAllowance | null,
): string | null => {
  if (!allowance || allowance.hasLimit !== true) {
    return null;
  }

  const { limit, amount } = topeBound(allowance.topePen);
  const pen = formatDepositMoney(amount, Currency.PEN);
  const usd = formatDepositMoney(allowance.topeUsd, Currency.USD);

  if (!pen || !usd) {
    return null;
  }

  return `Por ahora puedes depositar ${limit} ${pen} o ${usd}.`;
};

export const depositRemainingSentence = (
  remaining: number,
  currency: string,
): string => {
  if (!Number.isFinite(remaining) || remaining <= 0) {
    return 'Ya alcanzaste tu límite de depósito por ahora.';
  }

  return `Supera tu límite. Puedes depositar hasta ${formatDepositMoney(
    remaining,
    currency,
  )}.`;
};

/**
 * FIN-1710 · the pre-approval banner's own tail on the same figures (D87).
 *
 * The three exports above are FIN-1711's and are not edited — instruction of 2026-09-04.
 * This one sits beside them and reuses `formatDepositMoney`, `topePen` and `topeUsd`, so
 * the popup, the deposit screen, the validator and the banner keep quoting one cap.
 *
 * ⚠ No exchange rate. The handoff's *USD 1 515* divides by 3.3; the shipped cap runs on
 * 3.8 and the rate is FIN-1711 §9.11's, not this ticket's.
 */
export const preApprovalBannerLimitSentence = (
  allowance: DepositAllowance | null,
): string | null => {
  if (!allowance || allowance.hasLimit !== true) {
    return null;
  }

  const { limit, amount } = topeBound(allowance.topePen);
  const pen = formatDepositMoney(amount, Currency.PEN);
  const usd = formatDepositMoney(allowance.topeUsd, Currency.USD);

  if (!pen || !usd) {
    return null;
  }

  return `Por ahora ${limit} ${pen} o ${usd}, mientras terminamos de validar tu perfil.`;
};

/**
 * FIN-1710 · the 2nd banner's title — *"Tienes S/ 3 500 y USD 245 sin invertir"* (D109).
 *
 * The two figures arrive per-currency from `calculateUserAvailableBalance` as fixed-2
 * strings and are printed as they came: ⚠ **no exchange rate and no total**, because one
 * soles figure at a converted rate cannot express a two-currency sentence.
 *
 * A zero currency is dropped entirely, so one currency gives *"Tienes USD 400 sin
 * invertir"*. Both zero answers `null` — the caller falls back to approved copy rather
 * than rendering *"Tienes sin invertir"*.
 */
export const uninvestedBalanceSentence = (
  pen: string | number | null | undefined,
  usd: string | number | null | undefined,
): string | null => {
  const figures = [
    { amount: Number(pen), currency: Currency.PEN },
    { amount: Number(usd), currency: Currency.USD },
  ]
    .filter(figure => Number.isFinite(figure.amount) && figure.amount > 0)
    .map(figure => formatDepositMoney(figure.amount, figure.currency))
    .filter(sentence => !!sentence);

  if (!figures.length) {
    return null;
  }

  return `Tienes ${figures.join(' y ')} sin invertir`;
};
